import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ElasticSearchHistogramResponse, PCAFrameDetails } from 'c-sdk';
import {
    ElasticSearchHistogramInterval,
    EsPCAReportField,
    EsPCAReportFilterField,
    PCAReportMetric,
    PermissionName,
} from 'c-sdk';
import type {
    ReportConfig,
    ReportProps,
    ReportWrapperProps,
    TableReportHeader,
    TableReportRow,
} from 'c-reports/Types';
import { ReportDateTimeframe, ReportType } from 'c-reports/Types';
import { apiClient } from 'c-data';
import { differenceInDays, format, isSameDay, startOfDay } from 'date-fns';
import type { TypographyProps } from '@mui/material';
import { Box, Tooltip, Typography } from '@mui/material';
import { ReportWrapper } from 'c-reports/Components';
import { durationParts, formatHour } from 'c-lib';
import { datesInRange } from 'c-main/Lib';
import { useCommonTranslation } from 'c-translation';
import { useDateUtils } from 'c-hooks';
import { NumberFormatWhole } from 'c-components';
import { hourlyHistogramToOldDataStructure } from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/HistogramTransformers';
import { useAtom } from 'jotai/index';
import { atom_DateRange } from 'c-displays/atoms';
import useCampaignWrapperState from 'c-main/Components/Campaign/CampaignReports/useCampaignWrapperState';
import { generatePath } from 'react-router-dom';
import { PostAuthRoutes } from 'c-routes';
import JSURL from 'jsurl';
import { useUserPermissions } from 'c-auth-module/Hooks';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import {
    cornerHeaderSx,
    dayCellSx,
    dayHeaderSx,
    DIALOG_ELEVATION,
    hourCellSx,
    todayBadgeSx,
} from './frameGridSx';

type Props = {
    campaignId: number;
    metric: PCAReportMetric;
    field: string;
    frame: PCAFrameDetails;
    minDate: Date;
    maxDate: Date;
    environment: string;
};

const hours = Array.from(Array(24).keys());
const rowSx = { '&:hover td': { backgroundColor: 'action.hover' } };
const dayDateFormat = 'yyyy-MM-dd';
const dateBreakdownPermissions = [PermissionName.AdminCampaignFrame_date_breakdown];

type Totals = {
    actual: number;
    expected: number;
    receivedOutOfSchedule: number;
    receivedUnbooked: number;
};

const EsFrameByHourWeekly: React.FC<Props> = ({
    frame,
    metric,
    maxDate,
    minDate,
    campaignId,
    field,
    environment,
}) => {
    const { formatDateString } = useDateUtils();
    const [data, setData] = useState<ElasticSearchHistogramResponse>(null);
    const [currentStartDate, setCurrentStart] = useState<Date>(null);
    const [currentEndDate, setCurrentEnd] = useState<Date>(null);
    const { hasAll } = useUserPermissions();
    const t = useCommonTranslation();
    const transformedData = useMemo(
        () =>
            hourlyHistogramToOldDataStructure({
                metric,
                data,
                groupByField: false,
                onlyHourNumber: true,
            }),
        [metric, data],
    );
    const fetchData: ReportConfig<ElasticSearchHistogramResponse>['fetchData'] = useCallback(
        (start, end) =>
            apiClient.ReportsElasticSearch.histogram({
                campaignId,
                interval: ElasticSearchHistogramInterval.Hour,
                startDate: `${format(start, 'yyyy-MM-dd')} 00:00:00`,
                endDate: `${format(end, 'yyyy-MM-dd')} 23:59:59`,
                field: EsPCAReportField.FrameId,
                environment,
                fieldFilters: { [EsPCAReportFilterField.FrameId]: [frame?.frameId] },
            }),
        [campaignId, frame?.frameId, environment],
    );
    const onDataUpdated: ReportConfig<ElasticSearchHistogramResponse>['onDataUpdated'] =
        useCallback((data, start, end) => {
            setData(data);

            // make sure we don't needlessly update the references to the date objects
            setCurrentStart(current => (!isSameDay(current, start) ? start : current));
            setCurrentEnd(current => (!isSameDay(current, end) ? end : current));
        }, []);

    const [, setAtomTimeFrameValue] = useAtom(atom_DateRange);
    useEffect(() => {
        if (currentStartDate && currentEndDate) {
            setAtomTimeFrameValue({ startDate: currentStartDate, endDate: currentEndDate });
        }
    }, [currentStartDate, currentEndDate, setAtomTimeFrameValue]);

    const initialStartDate = useMemo(() => minDate, [minDate]);
    const initialEndDate = useMemo(() => maxDate, [maxDate]);

    const reportWrapperProps = useMemo<ReportWrapperProps<any>>(
        () => ({
            fetchData,
            removeLoadingBar: true,
            onDataUpdated,
            initialStartDate,
            initialEndDate,
            reportChartWrapperProps: { height: 400 },
            dateSettings: {
                timeframe: ReportDateTimeframe.All,
                minDate,
                maxDate,
                availableTimeframes: [
                    ReportDateTimeframe.Weekly,
                    ReportDateTimeframe.BiWeekly,
                    ReportDateTimeframe.Monthly,
                    ReportDateTimeframe.Range,
                ],
                onDatesUpdated: (timeframe, start, end) => {
                    setAtomTimeFrameValue({ startDate: start, endDate: end });
                },
            },
        }),
        [
            fetchData,
            onDataUpdated,
            initialStartDate,
            initialEndDate,
            minDate,
            maxDate,
            setAtomTimeFrameValue,
        ],
    );
    const allDatesInRange = useMemo(
        () =>
            datesInRange({
                start: currentStartDate,
                end: currentEndDate,
                returnAsDate: true,
            }) as Date[],
        [currentStartDate, currentEndDate],
    );
    const currentDate = useMemo(() => new Date(), []);
    const handleCellClick = useCallback(
        (day: Date) => {
            if (!frame?.displayId || !hasAll(dateBreakdownPermissions)) return;
            const state = {
                tab: 'date-breakdown',
                display: {
                    dateBreakdown: {
                        groupByMedia: true,
                        groupByHour: false,
                        timeframe: ReportDateTimeframe.Daily,
                        startDate: format(day, dayDateFormat),
                        endDate: format(day, dayDateFormat),
                    },
                },
            };
            const route = `${generatePath(PostAuthRoutes.Displays.DisplaysDisplayEdit, {
                id: frame.displayId,
            })}?state=${JSURL.stringify(state)}`;
            window.open(route, '_blank');
        },
        [frame?.displayId, hasAll],
    );

    // Where each day sits in the campaign: the first day of each campaign week, and today.
    const dayMarkers = useMemo(
        () =>
            allDatesInRange.map(day => {
                const sinceStart = differenceInDays(startOfDay(day), startOfDay(minDate));
                return {
                    isWeekStart: sinceStart >= 0 && sinceStart % 7 === 0,
                    weekNumber: 1 + sinceStart / 7,
                    isToday: differenceInDays(startOfDay(currentDate), startOfDay(day)) === 0,
                };
            }),
        [allDatesInRange, currentDate, minDate],
    );

    const tableHeaders = useMemo<TableReportHeader[]>(
        () => [
            {
                content: (
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {t('Modules.Main.Campaigns.Overview.Reports.frameList.hourColumn')}
                    </Typography>
                ),
                sx: cornerHeaderSx,
            },
            ...allDatesInRange.map((day, i) => {
                const { isWeekStart, weekNumber, isToday } = dayMarkers[i];
                return {
                    content: (
                        <Box
                            sx={{
                                width: '100%',
                                cursor:
                                    frame?.displayId && hasAll(dateBreakdownPermissions)
                                        ? 'context-menu'
                                        : 'default',
                                userSelect: 'none',
                            }}
                            onContextMenu={e => {
                                e.preventDefault();
                                handleCellClick(day);
                            }}
                        >
                            {/* The header always shows; the permission only gates the right-click. */}
                            <IfHasAllPermissions
                                permissions={
                                    frame?.displayId ? dateBreakdownPermissions : undefined
                                }
                                forceShow
                            >
                                <Box
                                    display="flex"
                                    justifyContent="flex-end"
                                    alignItems="center"
                                    gap={0.5}
                                    minHeight={18}
                                >
                                    {isWeekStart && (
                                        <Typography
                                            variant="overline"
                                            color="text.secondary"
                                            fontWeight={700}
                                            lineHeight={1}
                                        >
                                            {`${t(
                                                'Modules.Main.Campaigns.Overview.Reports.stickers.week',
                                            )} ${weekNumber}`}
                                        </Typography>
                                    )}
                                    {isToday && (
                                        <Box component="span" sx={todayBadgeSx}>
                                            {t(
                                                'Modules.Main.Campaigns.Overview.Reports.stickers.today',
                                            )}
                                        </Box>
                                    )}
                                </Box>
                                <Typography
                                    variant="caption"
                                    component="div"
                                    color="text.secondary"
                                    lineHeight={1.4}
                                    sx={{ textTransform: 'uppercase' }}
                                >
                                    {formatDateString({ date: day, dateFormatString: 'EEE' })}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="div"
                                    fontWeight={600}
                                    lineHeight={1.4}
                                    noWrap
                                >
                                    {formatDateString({ date: day, dateFormatString: 'do MMM' })}
                                </Typography>
                            </IfHasAllPermissions>
                        </Box>
                    ),
                    sx: dayHeaderSx({ isWeekStart, isToday }),
                };
            }),
        ],
        [
            allDatesInRange,
            dayMarkers,
            formatDateString,
            handleCellClick,
            frame?.displayId,
            hasAll,
            t,
        ],
    );

    const totalByDay = useMemo(
        () =>
            Object.entries(transformedData ?? {}).reduce((acc, [day, dayData]) => {
                acc[day] = Object.entries(dayData ?? {}).reduce(
                    (dayAcc, [_, hourData]) => {
                        const [actual, expected, , , receivedOutOfSchedule, receivedUnbooked] =
                            hourData;

                        return {
                            ...dayAcc,
                            actual: dayAcc.actual + actual,
                            expected: dayAcc.expected + expected,
                            receivedOutOfSchedule:
                                dayAcc.receivedOutOfSchedule + receivedOutOfSchedule,
                            receivedUnbooked: dayAcc.receivedUnbooked + receivedUnbooked,
                        };
                    },
                    {
                        actual: 0,
                        expected: 0,
                        receivedOutOfSchedule: 0,
                        receivedUnbooked: 0,
                    },
                );
                return acc;
            }, {}),
        [transformedData],
    );

    const totalByDayByHour = useMemo(
        () =>
            Object.entries(transformedData).reduce((acc, [day, dayData]) => {
                Object.entries(dayData ?? {}).forEach(([hour, hourData]) => {
                    const [actual, expected, , , receivedOutOfSchedule, receivedUnbooked] =
                        hourData;

                    acc[`${day}:${hour}`] = {
                        actual,
                        expected,
                        receivedOutOfSchedule,
                        receivedUnbooked,
                    };
                });
                return acc;
            }, {}),
        [transformedData],
    );

    const tableRows = useMemo<TableReportRow[]>(
        () => [
            {
                content: [
                    {
                        content: (
                            <Typography variant="caption" fontWeight={700}>
                                {t(`Modules.Main.Campaigns.Overview.Reports.frameList.dayOverall`)}
                            </Typography>
                        ),
                        sx: hourCellSx({ isOverall: true }),
                    },
                    ...allDatesInRange.map((day, i) => ({
                        content: (
                            <TotalsContent
                                totals={totalByDay[format(day, dayDateFormat)]}
                                metric={metric}
                            />
                        ),
                        sx: dayCellSx({ ...dayMarkers[i], isOverall: true }),
                    })),
                ],
                sx: rowSx,
            },
            ...hours.map(hour => ({
                content: [
                    {
                        content: (
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {formatHour(hour)}
                            </Typography>
                        ),
                        sx: hourCellSx(),
                    },
                    ...allDatesInRange.map((day, i) => ({
                        content: (
                            <TotalsContent
                                totals={totalByDayByHour[`${format(day, dayDateFormat)}:${hour}`]}
                                metric={metric}
                            />
                        ),
                        sx: dayCellSx(dayMarkers[i]),
                    })),
                ],
                sx: rowSx,
            })),
        ],
        [t, allDatesInRange, dayMarkers, totalByDay, metric, totalByDayByHour],
    );

    const rawData = useMemo(() => {
        const days = allDatesInRange.reduce((acc, curr) => {
            acc.push(format(curr, dayDateFormat));
            return acc;
        }, []);

        const dailyTotals = allDatesInRange.reduce((acc, curr) => {
            const dayKey = format(curr, dayDateFormat);
            const dayTotal = totalByDay[dayKey];

            if (dayTotal) {
                acc.push(`${dayTotal.actual} / ${dayTotal.expected}`);
            } else {
                acc.push('-');
            }
            return acc;
        }, []);

        const byHour = hours.reduce((acc, hour) => {
            const dayData = [formatHour(hour)];
            allDatesInRange.forEach(date => {
                const dateHourKey = `${format(date, dayDateFormat)}:${hour}`;
                const dayHourData = totalByDayByHour?.[dateHourKey];
                if (dayHourData) {
                    dayData.push(
                        `${String(dayHourData.actual ?? '-')} / ${String(
                            dayHourData.expected ?? '-',
                        )}`,
                    );
                } else {
                    dayData.push('-');
                }
            });

            acc.push(dayData);
            return acc;
        }, []);

        return [
            ['', ...days],
            [t(`Modules.Main.Campaigns.Overview.Reports.frameList.dayOverall`), ...dailyTotals],
            ...byHour,
        ];
    }, [allDatesInRange, totalByDayByHour, totalByDay, t]);

    const reportProps = useMemo<ReportProps<unknown>[]>(
        () => [
            {
                type: ReportType.Table,
                namePrefix: `${campaignId}_${metric}_${field}_leaderboard`,
                data: rawData,
                rows: tableRows,
                headers: tableHeaders,
                disableVirtualization: true,
                dense: true,
                // Always shown in the hourly breakdown Dialog (FieldFrameListEs).
                headerSurfaceElevation: DIALOG_ELEVATION,
            },
        ],
        [campaignId, field, metric, rawData, tableHeaders, tableRows],
    );
    const { onResetAdvancedFilters } = useCampaignWrapperState(campaignId);

    return (
        <>
            <Box flex={0.98} overflow="hidden" display="flex" width="100%">
                <ReportWrapper
                    {...reportWrapperProps}
                    reportProps={reportProps}
                    onResetAdvancedFilters={onResetAdvancedFilters}
                    wrapperProps={{
                        sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            overflow: 'hidden',
                        },
                    }}
                    reportWrapperProps={{ sx: { flex: 1, overflow: 'auto' } }}
                />
            </Box>
        </>
    );
};
const TotalsContent: React.FC<{ totals?: Totals; metric: PCAReportMetric }> = ({
    totals,
    metric,
}) => {
    const t = useCommonTranslation();

    const textColour = useMemo<TypographyProps['color']>(() => {
        if (!totals) return undefined;

        return Math.round(totals.expected) > 0
            ? Math.round(totals.actual) < Math.round(totals.expected)
                ? 'warning.main'
                : 'success.main'
            : Math.round(totals.receivedOutOfSchedule) > 0
            ? '#0185CD'
            : Math.round(totals.receivedUnbooked) > 0
            ? '#848081'
            : Math.round(totals.actual) < Math.round(totals.expected)
            ? 'warning.main'
            : 'success.main';
    }, [totals]);

    // Time is stored in seconds, so the cell shows seconds; the tooltip spells each figure out,
    // leaving out zero units ("24 minutes", not "0 hours, 24 minutes, 0 seconds").
    const timeTooltip = useMemo(() => {
        if (totals == null || metric !== PCAReportMetric.Time) return null;
        const duration = (seconds: number) =>
            durationParts(seconds)
                .map(({ unit, count }) => t(`Reporting.durationUnits.${unit}`, { count }))
                .join(', ');
        const prefix = 'Modules.Main.Campaigns.Overview.Reports.frameList';

        return (
            <>
                <Box>{t(`${prefix}.timePlayedTotal`, { duration: duration(totals.actual) })}</Box>
                <Box>
                    {t(`${prefix}.timePlayedExpected`, { duration: duration(totals.expected) })}
                </Box>
                <Box>
                    {t(`${prefix}.timePlayedOutOfSchedule`, {
                        duration: duration(totals.receivedOutOfSchedule),
                    })}
                </Box>
                <Box>
                    {t(`${prefix}.timePlayedOutOFHours`, {
                        duration: duration(totals.receivedUnbooked),
                    })}
                </Box>
            </>
        );
    }, [metric, t, totals]);
    if (!totals) {
        return (
            <Typography variant="caption" color="text.disabled">
                {t('Modules.Main.Campaigns.Overview.Reports.frameList.noHourData')}
            </Typography>
        );
    }
    const figures = (
        <Typography
            variant="caption"
            component="div"
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}
            noWrap
        >
            <Box component="span" color={textColour} fontWeight={700}>
                <NumberFormatWhole value={totals.actual} />
            </Box>
            <Box component="span" color="text.secondary">
                / <NumberFormatWhole value={totals.expected} />
            </Box>
        </Typography>
    );

    // The whole cell is the tooltip's target; a timer icon in every cell was just clutter.
    return timeTooltip ? <Tooltip title={timeTooltip}>{figures}</Tooltip> : figures;
};

export default EsFrameByHourWeekly;
